// Shared forum types + helpers.

export interface ForumUser {
  id: string;
  username: string | null;
  image: string | null;
  verificationTier: string | null;
}

export interface ForumCategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  emoji: string | null;
  position: number;
}

export interface ForumThread {
  id: string;
  categoryId: string;
  userId: string;
  title: string;
  body: string | null;
  ticker: string | null;
  score: number;
  commentCount: number;
  isPinned: boolean;
  isLocked: boolean;
  createdAt: string;
  lastActivityAt: string;
  editedAt: string | null;
  viewCount: number;
  myVote: number;
  isSaved?: boolean;
  isFollowing?: boolean;
  titleSr: string | null;
  titleEn: string | null;
  bodySr: string | null;
  bodyEn: string | null;
  User: ForumUser | null;
  ForumCategory: Pick<ForumCategory, 'id' | 'slug' | 'name' | 'emoji'> | null;
  lastComment?: {
    id: string;
    body: string;
    bodySr: string | null;
    bodyEn: string | null;
    createdAt: string;
    User: Pick<ForumUser, 'username'> | null;
  } | null;
}

export interface ForumComment {
  id: string;
  threadId: string;
  userId: string;
  parentId: string | null;
  body: string;
  score: number;
  isDeleted: boolean;
  createdAt: string;
  editedAt: string | null;
  myVote: number;
  bodySr: string | null;
  bodyEn: string | null;
  User: ForumUser | null;
}

// User-generated forum content (unlike fixed UI strings) is translated
// per-row by src/lib/forum/translate.ts and cached in the Sr/En columns.
// Falls back to the original text if the lazy translation hasn't landed yet.
export function localizedTitle(thread: Pick<ForumThread, 'title' | 'titleSr' | 'titleEn'>, lang: 'sr' | 'en'): string {
  return (lang === 'sr' ? thread.titleSr : thread.titleEn) ?? thread.title;
}

// A pinned title showing its own pin emoji (e.g. the hand-authored "📌 Forum rules")
// shouldn't get a second one from the UI's pinned indicator.
export function needsPinEmoji(thread: Pick<ForumThread, 'isPinned'>, title: string): boolean {
  return thread.isPinned && !title.trimStart().startsWith('📌');
}

export function localizedBody<T extends { body: string | null; bodySr: string | null; bodyEn: string | null }>(
  item: T,
  lang: 'sr' | 'en'
): string | null {
  return (lang === 'sr' ? item.bodySr : item.bodyEn) ?? item.body;
}

// DB timestamps are `timestamp without time zone` in UTC — append Z before parsing.
export function timeAgo(dateStr: string, lang: 'sr' | 'en'): string {
  const ts = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z').getTime();
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  const units: Array<[number, string, string]> = [
    [31536000, 'god', 'y'],
    [2592000, 'mes', 'mo'],
    [604800, 'ned', 'w'],
    [86400, 'd', 'd'],
    [3600, 'h', 'h'],
    [60, 'min', 'm'],
  ];
  for (const [secs, srU, enU] of units) {
    if (s >= secs) {
      const n = Math.floor(s / secs);
      return lang === 'sr' ? `pre ${n} ${srU}` : `${n}${enU} ago`;
    }
  }
  return lang === 'sr' ? 'upravo sada' : 'just now';
}

export async function voteOn(
  targetType: 'THREAD' | 'COMMENT',
  targetId: string,
  value: 1 | -1 | 0
): Promise<boolean> {
  const res = await fetch('/api/forum/vote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetType, targetId, value }),
  });
  return res.ok;
}
